'use client'

import { Box, Stack, Text, Textarea } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import reviewService from '@/_service/review.service'
import { toaster } from '@/components/ui/toaster'
import { useSelector } from 'react-redux'
import { IRedux, IUser } from '@/_store/types'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { Rating, RoundedStar } from '@smastrom/react-rating'
import Auth from '@/_components/Auth'
import BottomSheet from '@/_components/Sheet'
import { IBranch } from '@/_types'

const ReviewForm = ({ isOpen, onClose, branch, refetch }: { isOpen: boolean, onClose: () => void, branch: IBranch, refetch: () => void }) => {
  const t = useTranslations()
  const [rate, setRate] = useState(5)
  const [reviewMsg, setReviewMsg] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [isAuthDialog, setIsAuthDialog] = useState(false)

  const currentLocale = useCustomLocale()
  const { user } = useSelector((state: IRedux) => state.auth)

  const onSubmitReview = (userData?: IUser) => {
    if (!reviewMsg) {
      toaster.warning({
        title: t('successfully_sent'),
      })
      return
    }

    setLoading(true)

    const client = userData?.id ? userData : user
    const data = {
      lang: currentLocale,
      branch_id: branch.id,
      branch_name: branch.name,
      client_id: client?.id,
      client_mail: '',
      client_name: client?.name,
      client_phone: client?.phone,
      courier_id: '',
      courier_name: '',
      delivery_time: '',
      operator_id: '',
      operator_name: '',
      rating: rate,
      related_subject: 'branch',
      review_message: reviewMsg,
      type: rate > 3 ? 'like' : 'dislike',
    }

    reviewService
      .create(data)
      .then(() => {
        toaster.success({
          title: t('successfully_sent'),
        })
        onClose()
        refetch()
        setReviewMsg('')
      })
      .catch((error) => {
        console.log(error)
        toaster.error({
          title: t('error_occured'),
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} zIndex={45}>
      <>
        <Text color={'text'} px={4} fontSize='xl' fontWeight='bold'>
          {t('leave_feedback')}
        </Text>
        <Box p={4}>
          <Stack gap={4}>
            <Rating
              style={{ maxWidth: 150 }}
              value={rate}
              itemStyles={{
                itemShapes: RoundedStar,
                activeFillColor: '#FFC500',
                inactiveFillColor: '#E0E8F1',
              }}
              onChange={(newValue: number) => {
                setRate(newValue)
              }}
            />
            <Textarea
              size={'xl'}
              rounded={16}
              height={'100px'}
              variant='subtle'
              bgColor={'background'}
              placeholder={t('describe_your_impressions')}
              value={reviewMsg}
              onChange={(e) => setReviewMsg(e.target.value)}
            />

            <Button
              mt={2}
              onClick={() => {
                if (user) {
                  onSubmitReview()
                } else setIsAuthDialog(true)
              }}
              bg='primary'
              color='primary.text'
              _hover={{ bg: 'primary.hover' }}
              size={'2xl'}
              height={'56px'}
              disabled={rate < 1}
              loading={isLoading}
            >
              {t('send')}
            </Button>
          </Stack>
        </Box>
        <Auth
          open={isAuthDialog}
          onClose={() => setIsAuthDialog(false)}
          onLastConfirm={(data) => {
            onSubmitReview(data)
          }}
        />
      </>
    </BottomSheet>
  )
}

export default ReviewForm
